<template>
    <div>
        <div class="login-form-session">
            <p class="subtitle">Введите пин-код для продолжения работы</p>

            <div
                v-if="errorMessage"
                :class="['error-message', { 'error-message--warning': isAccessType4 }]"
            >
                {{ errorMessage }}
            </div>

            <form
                :id="`pin-form-${componentId}`"
                autocomplete="off"
                @submit.prevent
            >
                <!-- Обманные поля для браузера -->
                <input
                    type="text"
                    autocomplete="username"
                    tabindex="-1"
                    aria-hidden="true"
                    style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;"
                />
                <input
                    type="password"
                    autocomplete="new-password"
                    tabindex="-1"
                    aria-hidden="true"
                    style="position: absolute; left: -9999px; opacity: 0; pointer-events: none;"
                />
                <div class="pin-inputs">
                    <a-input
                        v-for="(digit, index) in pin"
                        :key="index"
                        ref="pinInputs"
                        :value="pinDisplay[index]"
                        class="pin-digit"
                        type="text"
                        :name="`pin-${componentId}-${index}`"
                        :id="`pin-${componentId}-${index}`"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        autocomplete="off"
                        autocapitalize="none"
                        autocorrect="off"
                        spellcheck="false"
                        :readonly="isInputReadonly"
                        :maxlength="1"
                        @input="onInput(index, $event)"
                        @keydown="handleKeyDown(index, $event)"
                        @focus="onFocus(index)"
                        @paste.prevent="onPaste(index, $event)"
                    />
                </div>
            </form>

            <a-button
                type="primary"
                class="login-btn"
                :disabled="!isPinComplete"
                @click="onSubmit"
            >
                Продолжить
            </a-button>

            <div class="forgot-pin">
                <a @click="onForgotPin">Забыли пин-код?</a>
            </div>
        </div>
    </div>
</template>

<script>
import { showErrorNotification } from '@/utils';

export default {
    name: 'SessionPinIn',
    data() {
        return {
            pin: ['', '', '', ''], // Реальные значения PIN
            pinDisplay: ['', '', '', ''], // Отображаемые значения (точки)
            isModalVisible: false,
            modalMessage: '',
            pinInputs: [],
            errorMessage: '',
            isAccessType4: false,
            isInputReadonly: true,
            componentId: `pin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };
    },
    computed: {
        isPinComplete() {
            return this.pin.every((digit) => digit !== '');
        },
    },
    mounted() {
        // Инициализируем защиту от автозаполнения для всех полей
        this.$nextTick(() => {
            this.applyAutocompleteProtection();
        });

        // Отключаем readonly через задержку для предотвращения автозаполнения
        setTimeout(() => {
            this.isInputReadonly = false;
            // Повторно применяем защиту после снятия readonly
            this.$nextTick(() => {
                this.applyAutocompleteProtection();
            });
        }, 500);

        // Устанавливаем фокус с дополнительной задержкой
        this.$nextTick(() => {
            setTimeout(() => {
                if (this.$refs.pinInputs && this.$refs.pinInputs[0]) {
                    this.$refs.pinInputs[0].focus();
                }
            }, 600);
        });
    },
    watch: {
        isPinComplete: {
            handler() {
                this.onSubmit();
            },
        },
    },
    methods: {
        onInput(index, event) {
            const { target: input } = event;
            const { value } = input;

            // Если введена цифра
            if (/^\d$/.test(value)) {
                // Сохраняем реальное значение
                this.pin[index] = value;
                // Отображаем точку
                this.pinDisplay[index] = '•';
                // Обновляем значение в поле ввода
                this.$nextTick(() => {
                    const inputElement = this.$refs.pinInputs[index]?.$el?.querySelector('input')
                        || this.$refs.pinInputs[index]?.$el;
                    if (inputElement) {
                        inputElement.value = '•';
                    }
                });

                // Сбрасываем модификатор при вводе нового пин-кода
                this.isAccessType4 = false;
                this.errorMessage = '';

                // Переходим к следующему полю
                if (index < 3) {
                    this.$nextTick(() => {
                        if (this.$refs.pinInputs[index + 1]) {
                            this.$refs.pinInputs[index + 1].focus();
                        }
                    });
                }
            } else {
                // Если введен не цифра, очищаем поле
                this.pin[index] = '';
                this.pinDisplay[index] = '';
                input.value = '';
            }
        },
        handleKeyDown(index, event) {
            const { key } = event;

            if (key === 'Delete' || key === 'Backspace') {
                this.onDelete(index);
                event.preventDefault();
            } else if (key === 'ArrowRight' && index < 3) {
                if (this.$refs.pinInputs[index + 1]) {
                    this.$refs.pinInputs[index + 1].focus();
                }
                event.preventDefault();
            } else if (key === 'ArrowLeft' && index > 0) {
                if (this.$refs.pinInputs[index - 1]) {
                    this.$refs.pinInputs[index - 1].focus();
                }
                event.preventDefault();
            }

            // Сбрасываем модификатор при изменении пин-кода
            if (key !== 'ArrowRight' && key !== 'ArrowLeft') {
                this.isAccessType4 = false;
                this.errorMessage = '';
            }
        },
        onDelete(index) {
            // Сбрасываем модификатор при удалении символов
            this.isAccessType4 = false;
            this.errorMessage = '';

            if (this.pin[index] === '') {
                if (index > 0) {
                    this.pin[index - 1] = '';
                    this.pinDisplay[index - 1] = '';
                    if (this.$refs.pinInputs[index - 1]) {
                        this.$refs.pinInputs[index - 1].focus();
                    }
                }
            } else {
                this.pin[index] = '';
                this.pinDisplay[index] = '';
            }
        },
        applyAutocompleteProtection() {
            if (!this.$refs.pinInputs) {
                return;
            }

            this.$refs.pinInputs.forEach((pinInput, index) => {
                const input = pinInput.$el?.querySelector('input') || pinInput.$el;
                if (input) {
                    // Агрессивная защита от автозаполнения
                    input.setAttribute('autocomplete', 'off');
                    input.setAttribute('name', `pin-${this.componentId}-${index}`);
                    input.setAttribute('id', `pin-${this.componentId}-${index}`);
                    input.setAttribute('data-lpignore', 'true');
                    input.setAttribute('data-form-type', 'other');
                    input.setAttribute('data-1p-ignore', 'true');
                    input.setAttribute('data-bwignore', 'true');
                    input.setAttribute('data-dashlane-ignore', 'true');
                    input.setAttribute('data-lastpass-ignore', 'true');
                    input.setAttribute('data-bitwarden-ignore', 'true');
                    input.setAttribute('data-keepass-ignore', 'true');
                    input.setAttribute('autocapitalize', 'none');
                    input.setAttribute('autocorrect', 'off');
                    input.setAttribute('spellcheck', 'false');
                    // Устанавливаем форму явно
                    input.setAttribute('form', `pin-form-${this.componentId}`);
                }
            });
        },
        onFocus(index) {
            this.$nextTick(() => {
                if (this.$refs.pinInputs[index]) {
                    const input = this.$refs.pinInputs[index].$el?.querySelector('input')
                        || this.$refs.pinInputs[index].$el;
                    if (input) {
                        // Дополнительная защита от автозаполнения при фокусе
                        input.setAttribute('autocomplete', 'off');
                        input.setAttribute('name', `pin-${this.componentId}-${index}-${Date.now()}`);
                        input.setAttribute('data-lpignore', 'true');
                        input.setAttribute('data-form-type', 'other');
                        input.setAttribute('data-1p-ignore', 'true');
                        input.setAttribute('data-bwignore', 'true');
                        input.setAttribute('data-dashlane-ignore', 'true');
                        input.setAttribute('data-lastpass-ignore', 'true');
                        input.setAttribute('data-bitwarden-ignore', 'true');
                        input.setAttribute('data-keepass-ignore', 'true');
                        input.setAttribute('form', `pin-form-${this.componentId}`);
                        // Устанавливаем правильное отображаемое значение
                        input.value = this.pinDisplay[index] || '';
                        input.select();
                    }
                }
            });
        },
        onPaste(index, event) {
            // Предотвращаем вставку из буфера обмена
            event.preventDefault();
            const pastedData = (event.clipboardData || window.clipboardData).getData('text');
            const digits = pastedData.replace(/\D/g, '').split('').slice(0, 4);
            digits.forEach((digit, i) => {
                if (index + i < 4 && /^\d$/.test(digit)) {
                    this.pin[index + i] = digit;
                    this.pinDisplay[index + i] = '•';
                }
            });
            // Обновляем отображаемые значения в полях
            this.$nextTick(() => {
                digits.forEach((digit, i) => {
                    if (index + i < 4 && /^\d$/.test(digit)) {
                        const inputElement = this.$refs.pinInputs[index + i]?.$el?.querySelector('input')
                            || this.$refs.pinInputs[index + i]?.$el;
                        if (inputElement) {
                            inputElement.value = '•';
                        }
                    }
                });
                // Фокус на последнее заполненное поле
                const lastIndex = Math.min(index + digits.length - 1, 3);
                if (this.$refs.pinInputs[lastIndex]) {
                    this.$refs.pinInputs[lastIndex].focus();
                }
            });
        },
        async onSubmit() {
            if (!this.isPinComplete) {
                return;
            }

            const { deviceId, staffId } = localStorage;
            const pinCode = this.pin.join('');

            try {
                const response = await this.$http.post('/v2/auth/pin/login', {
                    deviceId,
                    pin: pinCode,
                    userId: staffId,
                });

                const { user = {} } = response.data;

                if (Object.keys(user).length) {
                    const {
                        name,
                        previewFileId,
                        clinics,
                        id,
                        chatUserId,
                        chatUserToken,
                    } = user || {};

                    localStorage.username = name;
                    localStorage.previewFileId = previewFileId;
                    localStorage.clinics = clinics;
                    localStorage.staffId = id;
                    localStorage.chatUserId = chatUserId;
                    localStorage.chatUserToken = chatUserToken;
                }

                this.errorMessage = '';

                this.$emit('success');
            } catch (error) {
                this.pin = ['', '', '', ''];
                this.pinDisplay = ['', '', '', ''];
                // Сбрасываем модификатор при очистке пин-кода
                this.isAccessType4 = false;
                this.errorMessage = '';
                this.$nextTick(() => {
                    // Очищаем отображаемые значения в полях
                    this.$refs.pinInputs?.forEach((pinInput) => {
                        const input = pinInput.$el?.querySelector('input') || pinInput.$el;
                        if (input) {
                            input.value = '';
                        }
                    });
                    if (this.$refs.pinInputs[0]) {
                        this.$refs.pinInputs[0].focus();
                    }
                });

                if (error.response?.status === 401) {
                    this.$emit('expired');
                    return;
                }

                if (error.response?.data?.msg) {
                    this.errorMessage = error.response.data.msg;

                    const isAccessType1 = error.response.data.msg.includes('Доступ');
                    const isAccessType2 = error.response.data.msg.includes('PIN-код 5 раз');
                    const isAccessType3 = error.response.data.msg.includes('заблокирована');
                    const isAccessType5 = error.response.data.msg.includes('пин-код 5 раз');

                    if (isAccessType1 || isAccessType2 || isAccessType3 || isAccessType5) {
                        this.$emit('no-access');
                    }
                } else if (error?.response?.data?.message) {
                    this.isAccessType4 = error.response.data.message.includes('отключен');

                    if (this.isAccessType4) {
                        this.errorMessage = error.response.data.message;
                    } else {
                        this.openModalError(error.response.data.message);
                    }
                } else {
                    showErrorNotification('Ошибка. Попробуйте позже', 5000);
                }
            }
        },
        onForgotPin() {
            this.$emit('forgot-pin');
        },
        openModalError(msg = '') {
            this.modalMessage = msg;
            this.isModalVisible = true;
        },
    },
};
</script>

<style scoped lang="scss">
.login-form-session {
    display: flex;
    flex-direction: column;
    align-items: center;

    .ant-form-item {
        margin-bottom: 18px;
    }
    .ant-form-explain {
        margin-bottom: -17px;
        text-align: left;
        font-size: 13px;
    }

    h1 {
        font-size: 26px;
        font-weight: bold;
        margin-bottom: 8px;
        text-align: center;
    }

    .subtitle {
        color: #666;
        font-size: 14px;
        margin-bottom: 20px;
        text-align: center;
    }

    .pin-inputs {
        display: flex;
        justify-content: center;
        gap: 8px;
        margin-bottom: 20px;

        .pin-digit {
            width: 50px;
            height: 50px;
            text-align: center;
            font-size: 20px;
            border-radius: 8px;
            border: 1px solid #d9d9d9;

            &:focus {
                border-color: #83b274;
                box-shadow: 0 0 0 2px rgb(131, 178, 116, 0.2);
            }
        }

    }

    .error-message {
        color: #ff4d4f;
        font-size: 12px;
        margin-bottom: 15px;
        text-align: left;
        max-width: 100%;
        word-break: break-word; // Перенос длинных слов
        white-space: pre-wrap; // Сохраняет переносы строк
        line-height: 1.4;
        padding: 8px 10px;
        background: #fff5f5;
        border: 1px solid #ffccc7;
        border-radius: 4px;
        overflow-x: hidden;

        &--warning {
            color: #d46b08;
            background: #fffbe6;
            border-color: #ffe58f;
        }
    }

    .login-btn {
        min-width: 110px;
        height: auto;
        font-size: 14px;
        margin-bottom: 18px;
        max-width: fit-content;
        padding: 7px 20px;
        background-color: #8dc07c;
        text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.2);
        box-shadow: 0px 0px 4px rgba(46, 75, 37, 0.4);
        border: none;
        color: #fff;

        &:disabled,
        &[disabled] {
            pointer-events: none;
            opacity: 0.5;
        }

        &:hover {
            background-color: #74b85d;
        }
    }

    .forgot-pin {
        text-align: center;

        a {
            text-decoration: none;
            color: #83b274;
            font-size: 14px;
            transition: color 0.3s;

            &:hover {
                color: #74b85d;
            }
        }
    }

    img {
        display: block;
        margin: 0 auto 4px;
        max-width: 100px;
    }

    .app-title {
        display: block;
        color: #00706c;
        font-weight: 600;
        font-size: 14px;
        font-family: Arial, sans-serif !important;
        margin-bottom: 7px;
        text-align: center;
    }
}

/* Кастомизация модального окна */
.custom-modal .ant-modal-content {
    border-radius: 12px;
    padding: 20px;
    text-align: center;
}

.custom-modal .ant-modal-header {
    background-color: #f9f9f9;
    border-bottom: none;
    padding: 20px 24px;
    border-radius: 12px 12px 0 0;
}

.custom-modal .ant-modal-title {
    font-size: 18px;
    font-weight: bold;
    color: #333;
    line-height: 1.4;
    text-align: center;
}

.custom-modal .ant-modal-body {
    padding: 20px 24px;
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    text-align: center;
}

.custom-modal .ant-modal-footer {
    border-top: none;
    padding: 16px 24px;
    display: flex;
    justify-content: center;
}

/* Кнопка "Войти заново" */
.custom-ok-button {
    width: 100%;
    height: 40px;
    font-size: 16px;
    border-radius: 8px;
    background-color: #1890ff !important; // Ярко-синий как на скрине
    border: none !important;
    color: white !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: background-color 0.2s ease;

    &:hover {
        background-color: #40a9ff !important;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
    }
}
</style>

<style lang="scss">
    .ant-modal-root {
        &:has(.login-form-session) {
            .ant-modal-mask {
                backdrop-filter: blur(10px);
                -webkit-backdrop-filter: blur(10px);
            }
        }
    }
</style>
